import React, { useState } from "react";

import { Pager, SectionCard } from "../ui";

type YoutubeCategory = "about" | "videos";

type YoutubePageRows = {
  rows: Array<{ id: string; url: string }>;
  page: number;
  totalPages: number;
  start: number;
};

type YoutubeSectionProps = {
  getRowsForPage: (category: YoutubeCategory) => YoutubePageRows;
  getSummary: (category: YoutubeCategory) => string;
  invalidUrls: Record<YoutubeCategory, string[]>;
  onPageChange: (category: YoutubeCategory, page: number) => void;
  onChangeRow: (category: YoutubeCategory, rowId: string, value: string) => void;
  onAddRow: (category: YoutubeCategory) => void;
  onRemoveRow: (category: YoutubeCategory, rowId: string) => void;
  onReorderRows: (category: YoutubeCategory, fromIndex: number, toIndex: number) => void;
  onOpenPreview: () => void;
  onSave: () => void;
  saving: boolean;
};

/**
 * YouTube 링크 편집 섹션.
 * about/videos 카테고리별 행 편집, 드래그 정렬, 유효성 경고를 제공한다.
 */
export function YoutubeSection({
  getRowsForPage,
  getSummary,
  invalidUrls,
  onPageChange,
  onChangeRow,
  onAddRow,
  onRemoveRow,
  onReorderRows,
  onOpenPreview,
  onSave,
  saving,
}: YoutubeSectionProps) {
  const [dragging, setDragging] = useState<{ category: YoutubeCategory; fromIndex: number } | null>(null);
  const [dragOver, setDragOver] = useState<{ category: YoutubeCategory; toIndex: number } | null>(null);

  // 카테고리 한 컬럼의 페이지 행/정렬/검증 UI를 렌더링한다.
  function renderCategory(category: YoutubeCategory, label: string) {
    const pageRows = getRowsForPage(category);
    const warn = invalidUrls[category];

    return (
      <SectionCard
        className="cm-card-equal"
        title={label}
        actions={
          <button className="cm-btn cm-btn-secondary" onClick={() => onAddRow(category)} type="button">
            영상 추가
          </button>
        }
      >
        <div className="cm-youtube-card-body">
          <div className="cm-panel-grid cm-youtube-card-rows">
            {pageRows.rows.map((row, index) => {
              const absoluteIndex = pageRows.start + index;
              const isDragOver = dragOver?.category === category && dragOver?.toIndex === absoluteIndex;
              const isDragging = dragging?.category === category && dragging?.fromIndex === absoluteIndex;
              return (
                <div
                  className={`cm-row-editor ${isDragOver ? "drag-over" : ""} ${isDragging ? "is-dragging" : ""}`.trim()}
                  key={row.id}
                  style={{ gridTemplateColumns: "auto 1fr auto" }}
                  onDragOver={(event) => {
                    if (!dragging || dragging.category !== category) return;
                    event.preventDefault();
                    setDragOver({ category, toIndex: absoluteIndex });
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!dragging || dragging.category !== category) return;
                    onReorderRows(category, dragging.fromIndex, absoluteIndex);
                    setDragging(null);
                    setDragOver(null);
                  }}
                >
                  <button
                    className="cm-drag-handle"
                    draggable
                    onDragStart={() => {
                      setDragging({ category, fromIndex: absoluteIndex });
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setDragOver(null);
                    }}
                    title="드래그 정렬"
                    type="button"
                  >
                    ☰
                  </button>
                  <input
                    className="cm-input"
                    onChange={(event) => onChangeRow(category, row.id, event.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={row.url}
                  />
                  <button
                    className="cm-btn cm-btn-danger cm-btn-icon"
                    onClick={() => {
                      if (!window.confirm("YouTube 링크 행을 삭제하시겠습니까?")) return;
                      onRemoveRow(category, row.id);
                    }}
                    title="삭제"
                    type="button"
                  >
                    -
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cm-youtube-card-footer">
            <div className="cm-inline-row">
              <span className="cm-pill">{getSummary(category)}</span>
            </div>
            <Pager page={pageRows.page} totalPages={pageRows.totalPages} onPageChange={(page) => onPageChange(category, page)} />
            {warn.length > 0 ? (
              <div className="cm-notice warn">
                유효하지 않은 URL ({warn.length}개)
                <div className="cm-help" style={{ marginTop: 4 }}>
                  {warn.join(", ")}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>
    );
  }

  return (
    <div className="cm-panel-grid">
      <div className="cm-actions">
        <button className="cm-btn cm-btn-preview" onClick={onOpenPreview} type="button">
          미리보기
        </button>
        <button className="cm-btn" disabled={saving} onClick={onSave} type="button">
          {saving ? "저장 중..." : "YouTube 저장"}
        </button>
      </div>
      <div className="cm-grid-2">
        {renderCategory("about", "AboutUs 페이지")}
        {renderCategory("videos", "Videos 페이지")}
      </div>
    </div>
  );
}
